import { Fragment, ReactNode, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'

type ModalProps = {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    subTitle?: ReactNode;
    icon?: ReactNode;
    children?: ReactNode;
    maxWidth?: string; // Add prop to control max width
    className?: string; // Add prop to control additional class names
    easyToClose?: boolean;
};

export function Modal({ 
    isOpen, 
    onClose, 
    title, 
    subTitle, 
    icon, 
    children, 
    maxWidth = 'sm:max-w-lg', 
    className = "",
    easyToClose = true,
}: ModalProps) {
    const handleCancel = () => {
        onClose();
    };

    // prevent scroll propegation when modal is scrollable
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);
    
    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[999999]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className={`relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full ${maxWidth} sm:p-6 ${className}`}>
                                {/* Close button (X icon) */}
                                {easyToClose && (
                                    <div className="absolute top-0 right-0 pt-4 pr-4">
                                        <button
                                            type="button"
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                                        onClick={handleCancel}
                                    >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>
                                )}

                                {/* Header */}
                                <div>
                                    {icon && (
                                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                                            {icon}
                                        </div>
                                    )}

                                    <div className="mt-3 text-center sm:mt-5">
                                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                            {title}
                                        </Dialog.Title>
                                    </div>
                                </div>
                                
                                {/* Step Title */}
                                <div className="mb-4 text-center mt-2">
                                    <p className="text-sm text-gray-500">
                                        {subTitle}
                                    </p>
                                </div>

                                {/* Content */}
                                <div className="mt-5 sm:mt-6">
                                    {children}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

interface BackButtonProps {
    onClick: () => void;
}

export const BackButton = ({ onClick }: BackButtonProps) => {
    {/* Forced to be in top left */}
    return (
        <div className="absolute top-4 left-4 z-10">
            <button
                onClick={onClick}
                type="button"
                className="inline-flex items-center gap-x-1.5 text-md font-semibold text-gray-500 hover:text-main transition-colors duration-200 ease-in-out rounded-md whitespace-nowrap"
            >
                <ArrowLeftIcon className="h-4 w-4 stroke-[3]" aria-hidden="true" />
                Back
            </button>
        </div>
    );
};